import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Dropdown, IDropdownOption } from '@fluentui/react';
import { CalendarClockRegular, MoneyRegular, WarningRegular, ArrowTrendingRegular } from '@fluentui/react-icons';

import * as Functions from './FinancialPanel.Functions';
import { chartTooltip, useChartColors, useEchartsTheme } from '../chartTheme';

interface IProps {
    currency: string;
    projection: Functions.Projection;
    monthsAhead: number;
    onMonthsAheadChange: (months: number) => void;
    dsoDays?: number;
}


const HORIZON_OPTIONS: IDropdownOption[] = [
    { key: 3, text: 'Próximos 3 meses' },
    { key: 6, text: 'Próximos 6 meses' },
    { key: 12, text: 'Próximos 12 meses' }
];

/**
 * Bloque 3 — Proyección. Contesta "¿en qué mes me quedo corto?".
 *
 * No hay forecast en el sistema: esto no adivina ventas futuras. Proyecta dos cosas
 * que sí se saben. Una, cuándo se cobra lo que YA te deben, usando la demora real de
 * cobro del centro. Otra, cuánto viene entrando y saliendo por mes. Se muestran
 * separadas y nunca sumadas, porque sumarlas contaría dos veces la misma plata.
 */
export const ProjectionBlockComponent: React.FC<IProps> = ({
    currency,
    projection,
    monthsAhead,
    onMonthsAheadChange,
    dsoDays
}) => {
    const money = (value: number) => Functions.formatMoney(value, currency);
    const compact = (value: number) => Functions.formatCompactMoney(value, currency);
    // Colores del tema activo: comprometido azul, tendencia gris, ingreso verde, faltante rojo.
    const colors = useChartColors();
    const chartTheme = useEchartsTheme();
    const COMMITTED = colors.primary;
    const TREND = colors.textSecondary;
    const INCOME = colors.success;
    const BAD = colors.danger;
    const { points, schedule, trend, firstShortMonth } = projection;

    const committedInHorizon = points.reduce((acc, point) => acc + point.committedIncome, 0);

    const option = {
        tooltip: {
            trigger: 'axis',
            ...chartTooltip(colors),
            formatter: (params: any[]) => {
                const point = points[params[0].dataIndex];
                return `<b>${point.label}</b><br/>` +
                    `Cobranza comprometida: <b>${money(point.committedIncome)}</b><br/>` +
                    `Costos esperados: ${money(point.expectedOutcome)}<br/>` +
                    `Acumulado conservador: <b>${money(point.cumulativeCommitted)}</b><br/>` +
                    `Acumulado si sigue la tendencia: ${money(point.cumulativeTrend)}`;
            }
        },
        legend: {
            data: ['Cobranza comprometida', 'Costos esperados', 'Acumulado conservador', 'Acumulado con tendencia'],
            bottom: 0,
            textStyle: { fontSize: 11 }
        },
        grid: { left: 12, right: 12, top: 16, bottom: 40, containLabel: true },
        xAxis: { type: 'category', data: points.map((point) => point.label), axisLabel: { fontSize: 11 } },
        yAxis: [
            { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { lineStyle: { color: colors.gridLine } } },
            { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { show: false } }
        ],
        series: [
            {
                name: 'Cobranza comprometida',
                type: 'bar',
                barMaxWidth: 26,
                itemStyle: { color: INCOME, borderRadius: [3, 3, 0, 0] },
                data: points.map((point) => point.committedIncome)
            },
            {
                name: 'Costos esperados',
                type: 'bar',
                barMaxWidth: 26,
                itemStyle: { color: colors.negative, borderRadius: [3, 3, 0, 0] },
                data: points.map((point) => point.expectedOutcome)
            },
            {
                name: 'Acumulado conservador',
                type: 'line',
                yAxisIndex: 1,
                smooth: true,
                symbolSize: 6,
                itemStyle: { color: COMMITTED },
                markLine: {
                    silent: true,
                    symbol: 'none',
                    lineStyle: { color: BAD, type: 'dashed' },
                    data: [{ yAxis: 0 }],
                    label: { show: false }
                },
                data: points.map((point) => point.cumulativeCommitted)
            },
            {
                name: 'Acumulado con tendencia',
                type: 'line',
                yAxisIndex: 1,
                smooth: true,
                symbolSize: 5,
                lineStyle: { type: 'dashed' },
                itemStyle: { color: TREND },
                data: points.map((point) => point.cumulativeTrend)
            }
        ]
    };

    return (
        <div className="cs-card">
            <div className="cs-card__header">
                <div className="cs-card__title">Qué esperar en los próximos meses</div>
                <div className="cs-card__desc">
                    Dos escenarios, calculados con datos del sistema. Ninguno inventa ingresos que nadie comprometió.
                </div>
            </div>

            <div className="cs-card__toolbar">
                <Dropdown
                    styles={{ root: { minWidth: 200 } }}
                    options={HORIZON_OPTIONS}
                    selectedKey={monthsAhead}
                    onChange={(_event, option) => option && onMonthsAheadChange(Number(option.key))}
                />
                <span className="cs-kpi-cell__desc">
                    Ritmo de cobro usado: {dsoDays ? Functions.formatDays(dsoDays) : 'sin dato, se asume 30 días'}
                </span>
            </div>

            <div className="cs-kpi-grid">
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><MoneyRegular /></div>
                    <div className="cs-kpi-cell__value">{money(committedInHorizon)}</div>
                    <div className="cs-kpi-cell__label">Cobranza comprometida</div>
                    <div className="cs-kpi-cell__desc">Deuda ya generada que debería entrar en el horizonte</div>
                    {schedule.beyondHorizon > 0 && (
                        <div className="cs-kpi-cell__sub">{money(schedule.beyondHorizon)} caería después</div>
                    )}
                </div>
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><ArrowTrendingRegular /></div>
                    <div className="cs-kpi-cell__value">{money(trend.outcome)}</div>
                    <div className="cs-kpi-cell__label">Costo mensual</div>
                    <div className="cs-kpi-cell__desc">Promedio de egresos de los últimos meses</div>
                    <div className="cs-kpi-cell__sub">
                        {trend.months > 0 ? `Promediado sobre ${trend.months} ${trend.months === 1 ? 'mes' : 'meses'}` : 'Sin historia suficiente'}
                    </div>
                </div>
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><CalendarClockRegular /></div>
                    <div className="cs-kpi-cell__value">{money(trend.income)}</div>
                    <div className="cs-kpi-cell__label">Ingreso mensual</div>
                    <div className="cs-kpi-cell__desc">Promedio de ingresos de los últimos meses</div>
                    <div className="cs-kpi-cell__sub">
                        Neto del ritmo actual: {money(trend.net)}
                    </div>
                </div>
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><WarningRegular /></div>
                    <div className="cs-kpi-cell__value">
                        <span className={firstShortMonth ? 'fp-amount--negative' : undefined}>
                            {firstShortMonth ? firstShortMonth.label : 'Ninguno'}
                        </span>
                    </div>
                    <div className="cs-kpi-cell__label">Primer mes en rojo</div>
                    <div className="cs-kpi-cell__desc">Con el escenario conservador, cuándo deja de alcanzar</div>
                </div>
            </div>

            <div className="cs-card__body">
                <div className="fp-scenarios">
                    <div className="fp-scenario fp-scenario--committed">
                        <div className="fp-scenario__title">Escenario conservador</div>
                        <div className="fp-scenario__desc">
                            Entra sólo lo que ya te deben, cuando se espera cobrarlo según la demora real del centro. Los
                            costos siguen corriendo al ritmo actual. Es el piso: si acá no da, hay que actuar.
                        </div>
                    </div>
                    <div className="fp-scenario fp-scenario--trend">
                        <div className="fp-scenario__title">Escenario de tendencia</div>
                        <div className="fp-scenario__desc">
                            Si el centro sigue facturando y gastando como en los últimos {trend.months || 0} meses. No
                            proyecta crecimiento ni estacionalidad: repite el promedio.
                        </div>
                    </div>
                </div>

                {points.length === 0 ? (
                    <div className="cs-empty cs-empty--compact"><span>No hay datos suficientes para proyectar.</span></div>
                ) : (
                    <ReactECharts option={option} style={{ height: 320 }} theme={chartTheme} opts={{ renderer: 'svg' }} />
                )}

                {firstShortMonth && (
                    <div className="fp-alert">
                        <span className="fp-alert__icon" aria-hidden="true">!</span>
                        <span>
                            <strong>En {firstShortMonth.label} el acumulado conservador queda en {money(firstShortMonth.cumulativeCommitted)}.</strong>{' '}
                            Con lo que hoy te deben y el ritmo de costos actual, a partir de ese mes no alcanza. O se
                            acelera la cobranza, o se corre un costo, o entra facturación nueva.
                        </span>
                    </div>
                )}

                <div className="cs-table-wrap cs-block--divided">
                    <table className="cs-table">
                        <thead>
                            <tr>
                                <th>Mes</th>
                                <th className="cs-table__amount">Cobranza comprometida</th>
                                <th className="cs-table__amount">Costos esperados</th>
                                <th className="cs-table__amount">Neto conservador</th>
                                <th className="cs-table__amount">Acumulado conservador</th>
                                <th className="cs-table__amount">Acumulado con tendencia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {points.map((point) => (
                                <tr key={point.monthIndex}>
                                    <td>{point.label}</td>
                                    <td className="cs-table__amount">{money(point.committedIncome)}</td>
                                    <td className="cs-table__amount">{money(point.expectedOutcome)}</td>
                                    <td className="cs-table__amount">
                                        <span className={point.netCommitted < 0 ? 'fp-amount--negative' : undefined}>
                                            {money(point.netCommitted)}
                                        </span>
                                    </td>
                                    <td className="cs-table__amount">
                                        <span className={point.isShort ? 'fp-amount--negative' : 'fp-amount--positive'}>
                                            {money(point.cumulativeCommitted)}
                                        </span>
                                    </td>
                                    <td className="cs-table__amount">{money(point.cumulativeTrend)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="fp-source">
                    <span>
                        <strong>De dónde sale cada número.</strong> La cobranza comprometida es la deuda pendiente de
                        cada tramo de antigüedad, ubicada en el mes en que se espera cobrarla según la demora promedio
                        del centro ({dsoDays ? Functions.formatDays(dsoDays) : '30 días, por falta de dato'}). Los costos
                        esperados y el ingreso de tendencia son el promedio de los últimos {trend.months || 0} meses de
                        movimiento real. El acumulado arranca en cero: proyecta el flujo del período, no el saldo
                        bancario, que el sistema de cobranzas no conoce.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ProjectionBlockComponent;
